import {
  Legal,
  NotFound,
  ReadOnlyContext,
  ReportStyles,
  SuspensefulErrorBoundary,
  TitlePage,
  useTheme
} from '@threatadvisor/react-components';
import { CubeClient, useFileDownload, useGet, useTranslation } from '@threatadvisor/react-utils';
import { Client } from '@threatadvisor/react-utils/src/types/generated.Framework';
import { Affix, Alert, Button, Row, Space, Spin, Typography } from 'antd';
import { ThemeProvider } from 'antd-style';
import { format, parseISO } from 'date-fns';
import { default as React, ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import DetectionResponse from './DetectionResponse';
import StatusReport from './StatusReport';

type ReportPageProps = {
  securityEventApi: string;
  isManagedDetection?: boolean;
};
window['reportPageTimeout'] = {};

export default function ReportPage(props: ReportPageProps): ReactElement {
  const { securityEventApi } = props;
  const { t } = useTranslation();

  return (
    <ThemeProvider appearance={'light'}>
      <Row>
        <SuspensefulErrorBoundary
          errorFallback={err => (
            <Row style={{ width: '8.5in' }} justify="center">
              <div>
                <Alert
                  type="error"
                  message={t('errors.reportLoad', 'Failed to Load Report')}
                  description={err.message}
                />
              </div>
            </Row>
          )}
          loadingFallback={
            <Space
              style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <Row style={{ width: '8.5in' }} justify="center">
                <Spin style={{ marginTop: 50 }} />
              </Row>
              <Typography.Text>Loading...</Typography.Text>
            </Space>
          }
          onError={() => {
            // Set this so that the API will know to fail out instead of returning PDF with error message
            console.log('window.reportGenerationStatus: failed');
            window.reportGenerationStatus = 'failed';
          }}
          onLoad={() => {
            if (
              !window['chartRenders'] ||
              Object.entries(window['chartRenders']).some(([_k, v]) => v != 'Done')
            ) {
              window['reportPageTimeout'] && window.clearTimeout(window['reportPageTimeout']);
              window['reportPageTimeout'] = window.setTimeout(() => {
                delete window['reportPageTimeout'];
                delete window['chartRenders'];
                console.log('window.reportGenerationStatus: delayed completed');

                window.reportGenerationStatus = 'completed';
              }, 5000);

              return;
            }
            console.log('window.reportGenerationStatus: completed');
            window.reportGenerationStatus = 'completed';
          }}
        >
          <ReportPageSuspendable securityEventApi={securityEventApi} />
        </SuspensefulErrorBoundary>
      </Row>
    </ThemeProvider>
  );
}

function ReportPageSuspendable(props: ReportPageProps): ReactElement {
  const { securityEventApi } = props;
  const [searchparams] = useSearchParams();
  const {
    reportType,
    clientUid,
    reportStart,
    reportEnd,
    showTitle,
    title: reportTitle,
    subTitle: reportSubTitle
  } = Object.fromEntries(searchparams);

  const { isDarkMode } = useTheme();
  const { styles, cx } = ReportStyles({ isDarkMode });

  const { data: clientSettings, isLoading: clientSettingsLoading } = useGet<Client>(
    `${securityEventApi}/Settings/client/${clientUid}`,
    {
      loadIfDefined: clientUid
    }
  );

  const clientName = clientSettings?.name ?? '';

  let filename = 'Report.pdf';
  let reportComponent: ReactElement | null = null;
  //let reportTitle = 'Report';
  //let reportSubTitle;
  const reportDateTitle = getReportDatetitle();

  switch (reportType) {
    case 'statusReport':
      filename = `${clientName}_Case_Management_Status_${format(parseISO(reportStart || ''), 'yyyyMMdd')}`;
      reportComponent = (
        <StatusReport
          clientUid={clientUid || ''}
          reportStart={reportStart || ''}
          reportEnd={reportEnd || ''}
          securityEventApi={securityEventApi}
        />
      );
      break;
    case 'detectionResponse':
      filename = `${clientName}_Detection_Response_${format(parseISO(reportStart || ''), 'yyyyMMdd')}`;
      reportComponent = (
        <DetectionResponse
          clientUid={clientUid || ''}
          reportStart={reportStart || ''}
          reportEnd={reportEnd || ''}
          securityEventApi={securityEventApi}
          clientSettings={clientSettings}
        />
      );
      break;
    case 'status':
      reportComponent = <NotFound />;
      break;
  }

  function getReportDatetitle() {
    return `${format(parseISO(reportStart || ''), 'MM/dd/yyyy')} - ${format(parseISO(reportEnd || ''), 'MM/dd/yyyy')}`;
  }

  // Get the current URL
  const downloadUrl = new URL(window.location.href);
  // Remove the 'showTitle' parameter if it exists
  downloadUrl.searchParams.delete('showTitle');

  const [downloadPDF, loadingPDF] = useFileDownload(
    `${securityEventApi}/Report?${new URLSearchParams({
      url: encodeURIComponent(downloadUrl.toString()),
      clientName: clientName,
      titlePage: 'true',
      ...(reportType === 'detectionResponse' ? { tocPage: '1' } : null)
    })}`,
    {
      filename,
      contentType: 'application/pdf',
      onError: e => console.log(e)
    }
  );

  return reportType == 'title' ? (
    <div className={cx(styles.reportStyles)} style={{ display: 'flex', flexDirection: 'row' }}>
      <div id="report-container" cy-test="report-container">
        <TitlePage
          clientName={clientSettings?.name || 'ThreatAdvisor'}
          title={reportTitle}
          subtitle={reportSubTitle}
          dateTitle={reportDateTitle}
        />
      </div>
    </div>
  ) : (
    <ReadOnlyContext.Provider value={true}>
      <CubeClient apiEndpoint={`${securityEventApi}/cubejs-api`} tokenEndpoint={'token'}>
        <div className={cx(styles.reportStyles)} style={{ display: 'flex', flexDirection: 'row' }}>
          <div className="report-header">
            <Affix offsetTop={10}>
              <div style={{ border: '1px solid #e9e9e9', padding: 10 }}>
                <div className="report-title">{reportTitle}</div>
                <div className="report-subtitle">{reportSubTitle}</div>
                <Button
                  key="1"
                  type="primary"
                  loading={loadingPDF}
                  onClick={() => (clientSettings?.name != null ? downloadPDF() : undefined)}
                  disabled={clientSettingsLoading}
                >
                  Download
                </Button>
              </div>
            </Affix>
          </div>

          <div id="report-container" cy-test="report-container">
            {showTitle ? (
              <TitlePage
                clientName={clientSettings?.name || 'ThreatAdvisor'}
                title={reportTitle}
                subtitle={reportSubTitle}
                dateTitle={reportDateTitle}
              />
            ) : null}
            <Legal></Legal>
            {reportComponent}
          </div>
        </div>
      </CubeClient>
    </ReadOnlyContext.Provider>
  );
}
